import { useState, useCallback, useMemo } from 'react';
import {
  GridCallbackDetails,
  GridFilterModel,
  GridInitialState,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  useListPlaylists,
  PlaylistListItem,
  ListPlaylistsParams,
  PlaylistAttributeFilterDto,
  PlaylistFiltersDto,
  DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
} from '@/app2/shared/data';

const SORT_FIELDS: readonly string[] = ['title'];

const FILTERABLE_FIELDS: readonly string[] = ['title'];

interface usePlaylistsListGridProps {
  initialFilterSettings: ListPlaylistsParams;
  onFilterModelChange?: (filterModel: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
}

interface UsePlaylistsListGridResult {
  rows: PlaylistListItem[];
  rowCount: number;
  isLoading: boolean;
  refetch: () => void;
  page: number;
  pageSize: number;
  sortModel: GridSortModel;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortModelChange: (model: GridSortModel) => void;
  onFilterChange: (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
  defaultInitialState: GridInitialState;
  selectionModel: any[];
  setSelectionModel: (model: any[]) => void;
}

export const usePlaylistsListGrid = ({
  initialFilterSettings,
  onFilterModelChange,
}: usePlaylistsListGridProps): UsePlaylistsListGridResult => {
  const [filterModel, setFilterModel] = useState<ListPlaylistsParams>(initialFilterSettings);
  const [selectionModel, setSelectionModel] = useState<any[]>([]);

  const { data, isLoading, refetch } = useListPlaylists(filterModel);

  const rows = data?.data ?? [];
  const rowCount = data?.totalCount ?? 0;

  const onPageChange = useCallback((page: number) => {
    setFilterModel((prev) => ({
      ...prev,
      page: page + 1,
    }));
  }, []);

  const onPageSizeChange = useCallback((pageSize: number) => {
    setFilterModel((prev) => ({
      ...prev,
      pageSize,
      page: 1,
    }));
  }, []);

  const onFilterChange = useCallback(
    (newFilterModel: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
      setFilterModel((prev) => {
        const filters: PlaylistAttributeFilterDto[] = (newFilterModel.items ?? [])
          .filter(
            (item: {
              columnField?: string;
              field?: string;
              value?: unknown;
              operatorValue?: string;
              operator?: string;
            }) => {
              const field = item.columnField ?? item.field;
              const hasValue =
                item.value !== undefined && item.value !== null && String(item.value).trim() !== '';
              const operator = item.operatorValue ?? item.operator ?? 'co';
              return field && FILTERABLE_FIELDS.includes(field) && (operator === 'pr' || hasValue);
            },
          )
          .map(
            (item: {
              columnField?: string;
              field?: string;
              value?: unknown;
              operatorValue?: string;
              operator?: string;
            }) => {
              const field = (item.columnField ?? item.field) as NonNullable<PlaylistAttributeFilterDto['field']>;
              const operator = (item.operatorValue ??
                item.operator ??
                'co') as NonNullable<PlaylistAttributeFilterDto['operator']>;
              let value: string | undefined;
              if (operator === 'pr') {
                value = undefined;
              } else if (item.value instanceof Date) {
                value = item.value.toISOString();
              } else {
                value = String(item.value ?? '');
              }
              return { field, value, operator } satisfies PlaylistAttributeFilterDto;
            },
          );

        onFilterModelChange?.(newFilterModel, details);

        const nextFilters: PlaylistFiltersDto | undefined =
          filters.length > 0
            ? {
                operator: (newFilterModel.linkOperator ?? 'and') as NonNullable<PlaylistFiltersDto['operator']>,
                conditions: filters,
              }
            : undefined;

        return {
          ...prev,
          page: 1,
          filters: nextFilters,
        };
      });
    },
    [onFilterModelChange],
  );

  const onSortModelChange = useCallback((sortModel: GridSortModel) => {
    setFilterModel((prev) => ({
      ...prev,
      sortField:
        sortModel.length > 0 && SORT_FIELDS.includes(sortModel[0].field) ? sortModel[0].field : undefined,
      sortDirection: sortModel.length > 0 ? (sortModel[0].sort as 'asc' | 'desc') : 'desc',
    }));
  }, []);

  const sortModel = useMemo<GridSortModel>(
    () =>
      filterModel.sortField
        ? [{ field: filterModel.sortField, sort: filterModel.sortDirection ?? 'desc' }]
        : [],
    [filterModel.sortField, filterModel.sortDirection],
  );

  const defaultInitialState: GridInitialState = {
    pagination: {
      pageSize: filterModel.pageSize ?? DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
      page: (filterModel.page ?? 1) - 1,
    },
    sorting: {
      sortModel: [],
    },
  };

  return {
    rows,
    rowCount,
    isLoading,
    refetch,
    page: (filterModel.page ?? 1) - 1,
      pageSize: filterModel.pageSize ?? DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
    sortModel,
    onPageChange,
    onPageSizeChange,
    onSortModelChange,
    onFilterChange,
    defaultInitialState,
    selectionModel,
    setSelectionModel,
  };
};
